/**
 * The carry family (R25) — `take`, `drop`, the acquisition record, and the
 * capacity gate, split from `action-handlers.ts` at the 500-line cap (the
 * container-handlers precedent; `action-handlers` re-exports, so no caller
 * changed).
 *
 * @remarks
 * The two verbs are one idea pointed two ways: `take` moves a thing from the
 * world to the hands and writes history (the acquisition record — where and
 * when it was FIRST taken, never rewritten); `drop` moves it back, into the
 * dropped-item record that Mike's discoverability rule reads (a dropped item
 * is found again by SEARCHING the room it lies in, until and unless it is
 * taken again). The capacity gate is a primitive the engine carries and no
 * pack gets uninvited: absent a declared `carryCapacity`, nothing refuses.
 *
 * @packageDocumentation
 */

import type { ItemId, RuntimeState } from '@orpheus/content-schema';

import type { RuntimeAction } from '../actions/runtime-action.js';
import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';

import { applicableDoorRefusal } from '../actions/narrow-parsed-command.js';
import { requireItem } from '../bundle/bundle-lookups.js';
import { speakSlot } from '../defaults/default-responses.js';
import { unequipItem } from '../entities/item-state.js';
import { type ActionHandlerResult, completedSpeaking, rejected } from './action-handler-result.js';
import { drawFromStock } from './stock-draw.js';
import { type TranscriptCursor } from './transcript-cursor.js';

/**
 * `take` — the refusal ladder (already held, ambient, fixture, hands full),
 * then the world delta, the acquisition record, and the item's take channel.
 */
export const handleTake = (
	bundle: EngineGameBundle,
	state: RuntimeState,
	action: Extract<RuntimeAction, { type: 'take' }>,
	cursor: TranscriptCursor,
): ActionHandlerResult => {
	const item = requireItem(bundle, action.itemId);

	if (state.world.inventoryItemIds.includes(action.itemId)) {
		return rejected(bundle, state, { itemId: action.itemId, type: 'already_held' }, [
			speakSlot(bundle, state, 'alreadyHeld', { itemName: item.name }),
		]);
	}

	// D199: the thing's own refusal, when it applies — before portability, so
	// a portable thing may refuse for now and a fixed one may refuse in its own
	// words rather than the pack's one sentence for every fixed thing.
	const takeRefusal = applicableDoorRefusal(item.takeRefusal, bundle, state);

	if (takeRefusal !== undefined) {
		return rejected(bundle, state, { itemId: action.itemId, type: 'take_refused' }, takeRefusal.narratives);
	}

	// D244: a stock is taken FROM — the draw's own ladder, after the thing's
	// refusal and before the fixed-thing line it would otherwise earn.
	if (item.stock !== undefined) {
		return drawFromStock(bundle, state, item, item.stock, cursor);
	}

	// Ambient items are never takeable (D19b) — checked before portability so
	// the rule holds even if a pack ever marks an ambient item portable.
	if (bundle.contentPack.ambientItemIds.includes(action.itemId) || !item.isPortable) {
		return rejected(bundle, state, { itemId: action.itemId, type: 'not_portable' }, [speakSlot(bundle, state, 'notPortable')]);
	}

	// R25: the capacity gate — bulk-based, and only for packs that declare a
	// ceiling (absent = unlimited, so no pre-R25 pack changes behavior).
	// `drop` is the relief valve, which is why the refusal exists at all.
	if (wouldExceedCapacity(bundle, state, action.itemId)) {
		return rejected(bundle, state, { itemId: action.itemId, type: 'capacity_exceeded' }, [
			speakSlot(bundle, state, 'capacityExceeded', { itemName: item.name }),
		]);
	}

	// R25: re-taking a DROPPED item lifts it from where it was set down; a
	// first take suppresses its authored placement instead. The two are
	// mutually exclusive by construction — a dropped item is never still in
	// its authored placement.
	const wasDropped = state.world.droppedItemLocations[action.itemId] !== undefined;
	const { [action.itemId]: _liftedRoomId, ...remainingDropped } = state.world.droppedItemLocations;

	// D98: the record's write-once semantics are the narrative layer's fact
	// too. `takeNarratives` were authored for the item's PLACEMENT, and only
	// the FIRST acquisition stands there — a re-take anywhere else would speak
	// them as lies ("You free the bar from the wreck of the shelving", in a
	// corridor with no shelving — Calder's finding). First-ness is the
	// acquisition record's absence, not `wasDropped`: the record is the one
	// authority on whether this take is history being written or revisited.
	const isFirstAcquisition = !state.world.acquisitions.some((acquisition) => acquisition.itemId === action.itemId);
	const spokenTakeLines = isFirstAcquisition
		? item.takeNarratives ?? [speakSlot(bundle, state, 'takeAcknowledgement', { itemName: item.name })]
		: item.retakeNarratives ?? [speakSlot(bundle, state, 'retakeAcknowledgement', { itemName: item.name })];

	return completedSpeaking(
		bundle,
		{
			...state,
			world: {
				...state.world,
				acquisitions: withAcquisition(state, action.itemId),
				droppedItemLocations: wasDropped ? remainingDropped : state.world.droppedItemLocations,
				inventoryItemIds: [...state.world.inventoryItemIds, action.itemId],
				removedRoomItemIds: wasDropped
					? state.world.removedRoomItemIds
					: [...state.world.removedRoomItemIds, action.itemId],
			},
		},
		cursor,
		spokenTakeLines,
		`take:${action.itemId}`,
	);
};

/**
 * `drop` (R25) — the take shape, pointed the other way: the item leaves the
 * hands, lies where it was set down, and is takeable again. Mike's
 * discoverability rule rides on the record this writes: the room's `search`
 * names what was set down here, until each thing is taken again.
 */
export const handleDrop = (
	bundle: EngineGameBundle,
	state: RuntimeState,
	action: Extract<RuntimeAction, { type: 'drop' }>,
	cursor: TranscriptCursor,
): ActionHandlerResult => {
	const item = requireItem(bundle, action.itemId);

	// The one honest refusal — and it never mentions searching: the player is
	// not looking for the thing, they are not holding it.
	if (!state.world.inventoryItemIds.includes(action.itemId)) {
		return rejected(bundle, state, { itemId: action.itemId, type: 'not_carried' }, [
			speakSlot(bundle, state, 'notCarried', { itemName: item.name }),
		]);
	}

	// D143: a worn thing comes off before it is set down — silently; the drop
	// line is the event.
	const unworn = unequipItem(state, action.itemId);

	return completedSpeaking(
		bundle,
		{
			...unworn,
			world: {
				...unworn.world,
				droppedItemLocations: {
					...unworn.world.droppedItemLocations,
					[action.itemId]: unworn.progression.activeRoomId,
				},
				inventoryItemIds: unworn.world.inventoryItemIds.filter((carriedItemId) => carriedItemId !== action.itemId),
			},
		},
		cursor,
		// Calder's §1.3 hook: an item may have an opinion about being set down
		// — the takeNarratives pattern exactly, slot fallback when it does not.
		item.dropNarratives ?? [speakSlot(bundle, state, 'dropAcknowledgement', { itemName: item.name })],
		`drop:${action.itemId}`,
	);
};

/**
 * The acquisition record's one writer (R25): append on FIRST take only —
 * "found here" is history, and history is never rewritten. The turn stamped
 * is the same value this turn's transcript entries carry.
 *
 * @param state - The state before the take
 * @param itemId - The item entering the inventory
 * @returns The record, appended to or unchanged
 */
const withAcquisition = (state: RuntimeState, itemId: ItemId): RuntimeState['world']['acquisitions'] =>
	state.world.acquisitions.some((acquisition) => acquisition.itemId === itemId)
		? state.world.acquisitions
		: [
			...state.world.acquisitions,
			{ itemId, roomId: state.progression.activeRoomId, turnCount: state.progression.turnCount },
		];

/**
 * Would taking this item exceed the pack's declared carry capacity? (R25 —
 * `false` whenever no ceiling is declared: capacity is a primitive the engine
 * carries, never a behavior packs get uninvited.)
 *
 * @param bundle - The loaded bundle (the manifest's ceiling; items' bulk)
 * @param state - The state before the take
 * @param itemId - The item being taken
 * @returns Whether the take must refuse
 */
const wouldExceedCapacity = (bundle: EngineGameBundle, state: RuntimeState, itemId: ItemId): boolean => {
	const capacity = bundle.contentPack.manifest.carryCapacity;

	if (capacity === undefined) {
		return false;
	}

	const carriedBulk = state.world.inventoryItemIds.reduce(
		(total, carriedItemId) => total + (requireItem(bundle, carriedItemId).bulk ?? 1),
		0,
	);

	return carriedBulk + (requireItem(bundle, itemId).bulk ?? 1) > capacity;
};
